import * as api from "./client";

export type MachineAccount = {
  id: string;
  name: string;
  email: string;
  status: string;
  createdAt: string;
  updatedAt: string;
};

export type MachineAccountSecret = {
  id: string;
  userId: string;
  label: string;
  createdAt: string;
  expiresAt: string | null;
  lastUsedAt: string | null;
  revokedAt: string | null;
};

export type CreatedSecret = {
  id: string;
  label: string;
  secret: string;
};

type ApiListResponse = {
  data: MachineAccount[];
  meta: { total: number; limit: number; offset: number };
};

type ApiSingleResponse = {
  data: MachineAccount;
};

type ApiSecretsResponse = {
  data: MachineAccountSecret[];
};

type ApiCreatedSecretResponse = {
  data: CreatedSecret;
};

type ApiRolesResponse = {
  data: { roles: string[] };
};

export type CreateMachineAccountRequest = {
  name: string;
};

export type PatchMachineAccountRequest = {
  name?: string;
  status?: string;
};

export type SetRolesRequest = {
  roles: string[];
};

export type AuthenticateRequest = {
  machineAccountId: string;
  secret: string;
};

export type MachineAccountsListResult = {
  accounts: MachineAccount[];
  total: number;
};

export type MachineAccountsFilters = {
  search?: string;
  limit?: number;
  offset?: number;
};

export const machineAccounts = {
  getAll: async (filters?: MachineAccountsFilters): Promise<MachineAccountsListResult> => {
    const params = new URLSearchParams();
    if (filters?.search) params.append("search", filters.search);
    if (filters?.limit !== undefined) params.append("limit", filters.limit.toString());
    if (filters?.offset !== undefined) params.append("offset", filters.offset.toString());

    const query = params.toString() ? `?${params.toString()}` : "";
    const response = await api.get<ApiListResponse>(`/v1/machine-accounts${query}`);
    return { accounts: response.data, total: response.meta.total };
  },

  getById: async (id: string): Promise<MachineAccount> => {
    const response = await api.get<ApiSingleResponse>(`/v1/machine-accounts/${id}`);
    return response.data;
  },

  create: async (data: CreateMachineAccountRequest): Promise<MachineAccount> => {
    const response = await api.post<ApiSingleResponse>("/v1/machine-accounts", data);
    return response.data;
  },

  update: async (id: string, data: PatchMachineAccountRequest): Promise<MachineAccount> => {
    const response = await api.patch<ApiSingleResponse>(`/v1/machine-accounts/${id}`, data);
    return response.data;
  },

  getSecrets: async (id: string): Promise<MachineAccountSecret[]> => {
    const response = await api.get<ApiSecretsResponse>(`/v1/machine-accounts/${id}/secrets`);
    return response.data;
  },

  createSecret: async (id: string, label?: string): Promise<CreatedSecret> => {
    const response = await api.post<ApiCreatedSecretResponse>(
      `/v1/machine-accounts/${id}/secrets`,
      label ? { label } : {},
    );
    return response.data;
  },

  revokeSecret: async (id: string, secretId: string): Promise<void> => {
    await api.del(`/v1/machine-accounts/${id}/secrets/${secretId}`);
  },

  getRoles: async (id: string): Promise<string[]> => {
    const response = await api.get<ApiRolesResponse>(`/v1/machine-accounts/${id}/roles`);
    return response.data.roles;
  },

  setRoles: async (id: string, roleContext: string, roles: string[]): Promise<void> => {
    await api.put(`/v1/machine-accounts/${id}/roles`, { context: roleContext, roles });
  },

  authenticate: async (machineAccountId: string, secret: string): Promise<unknown> => {
    return api.post("/v1/machine-accounts/authenticate", { machineAccountId, secret });
  },
};
